import React, { useState } from 'react';
import { View, Text, Button } from '@tarojs/components';
import { GameSettings, DIFFICULTY_LABELS, CATEGORY_LABELS, MATH_TOPIC_LABELS, MATH_TOPIC_GROUPS, MathTopic, normalizeSettings } from '@/data/settings';
import { questionsIn } from '@/data/questions';
import styles from './index.module.scss';

const SettingsPanel: React.FC<{ value: GameSettings; onChange: (settings: GameSettings) => void }> = ({ value, onChange }) => {
  const update = (patch: Partial<GameSettings>) => onChange(normalizeSettings({ ...value, ...patch }));
  const [showMathTopics, setShowMathTopics] = useState(false);
  const challenge = value.mode === 'challenge';
  const propSets = Math.min(value.props.remove, value.props.undo, value.props.shuffle);
  const mixedProps = value.props.remove !== value.props.undo || value.props.undo !== value.props.shuffle;
  const counter = (label: string, count: number, change: (amount: number) => void, disabled = false) => (
    <View className={styles.row}>
      <Text className={styles.label}>{label}</Text>
      <View className={styles.counter}>
        <Button aria-label={label + '减少'} disabled={disabled || count === 0} onClick={() => change(-1)}>−</Button>
        <Text>{count}</Text>
        <Button aria-label={label + '增加'} disabled={disabled || count === 9} onClick={() => change(1)}>＋</Button>
      </View>
    </View>
  );
  const toggle = (label: string, key: 'soundEnabled' | 'musicEnabled' | 'animationsEnabled' | 'showRemaining') => (
    <View className={styles.row}>
      <Text className={styles.label}>{label}</Text>
      <Button aria-label={label} className={styles.toggle + (value[key] ? ' ' + styles.on : '')}
        onClick={() => update({ [key]: !value[key] })}
      >{value[key] ? '开启' : '关闭'}</Button>
    </View>
  );
  return (
    <View className={styles.settings}>
      <Text className={styles.note}>玩法设置在下一局生效。声音、动画和剩余张数立即生效；自动保存。</Text>
      <Text className={styles.heading}>游玩模式</Text>
      <View className={styles.choices}>
        {(['props', 'challenge'] as const).map(mode => (
          <Button key={mode} className={value.mode === mode ? styles.selected : ''} onClick={() => update({ mode })}>
            {mode === 'props' ? '道具模式' : '无道具挑战'}
          </Button>
        ))}
      </View>
      <Text className={styles.hint}>{challenge ? '禁止所有道具、复活及答题奖励。切回道具模式会恢复自定义次数。' : '1套包含移出、撤回、洗牌各1次；复活次数单独设置。'}</Text>
      <Text className={styles.heading}>难度</Text>
      <View className={styles.choices}>
        {(Object.keys(DIFFICULTY_LABELS) as GameSettings['difficulty'][]).map(difficulty => (
          <Button key={difficulty} className={value.difficulty === difficulty ? styles.selected : ''} onClick={() => update({ difficulty })}>
            {DIFFICULTY_LABELS[difficulty]}
          </Button>
        ))}
      </View>
      <Text className={styles.hint}>每日一关偏轻松，大世界采用深层城堡、盲牌和辅助区。简单减少交错，地狱提高规划压力；道具用于腾挪与补救。</Text>
      <Text className={styles.heading}>每局初始次数</Text>
      {counter('初始道具（套）', challenge ? 0 : propSets, amount => update({ props: {
        ...value.props, remove: propSets + amount, undo: propSets + amount, shuffle: propSets + amount
      } }), challenge)}
      {mixedProps && <Text className={styles.hint}>旧设置保留：移出{value.props.remove}、撤回{value.props.undo}、洗牌{value.props.shuffle}；调整套数后统一。</Text>}
      {counter('初始复活（次）', challenge ? 0 : value.props.revive, amount => update({ props: { ...value.props, revive: value.props.revive + amount } }), challenge)}
      <Text className={styles.heading}>答题规则</Text>
      <View className={styles.quizChoices}>
        {(['off', 'refill', 'always'] as const).map(quizMode => (
          <Button key={quizMode} disabled={challenge} className={value.quizMode === quizMode ? styles.selected : ''}
            onClick={() => update({ quizMode })}
          >{{ off: '无需答题', refill: '用完后答题补给', always: '每次使用前答题' }[quizMode]}</Button>
        ))}
      </View>
      {!challenge && value.quizMode !== 'off' && <>
      {counter('答题道具（套）', value.quizLimit, amount => update({ quizLimit: value.quizLimit + amount }))}
      {counter('答题复活（次）', value.reviveQuizLimit, amount => update({ reviveQuizLimit: value.reviveQuizLimit + amount }))}
      <Text className={styles.hint}>1套表示移出、撤回、洗牌各可答题获得1次，分别计数；答题复活单独设置、单独计数，每次移出3张。为已有道具答题不占额外额度。</Text>
      <Text className={styles.hint}>提交后才能看答案与解析，选项随即锁定。答错只能下一题或放弃，答对后确认领取。</Text>
      <Text className={styles.heading}>题库分类</Text>
      <View className={styles.choices + ' ' + styles.categories}>
        {(Object.keys(CATEGORY_LABELS) as GameSettings['questionCategory'][]).map(questionCategory => (
          <Button key={questionCategory} disabled={challenge || value.quizMode === 'off'}
            className={value.questionCategory === questionCategory ? styles.selected : ''} onClick={() => update({ questionCategory })}
          >{CATEGORY_LABELS[questionCategory]}</Button>
        ))}
      </View>
      {(value.questionCategory === 'math' || value.questionCategory === 'all') && <>
        <Text className={styles.heading}>数学题型</Text>
        <Button aria-label='选择数学题型' className={styles.topicPicker} onClick={() => setShowMathTopics(v => !v)}>
          {MATH_TOPIC_LABELS[value.mathTopic]} {showMathTopics ? '▴' : '▾'}
        </Button>
        {showMathTopics && <View data-math-topics='true'>
          {[{ label: '', topics: ['all'] as MathTopic[] }, ...MATH_TOPIC_GROUPS].map(group => <View key={group.label}>
            {group.label && <Text className={styles.topicGroup}>{group.label}</Text>}
            <View className={styles.choices + ' ' + styles.categories}>
              {group.topics.map(mathTopic => <Button key={mathTopic}
                className={value.mathTopic === mathTopic ? styles.selected : ''}
                onClick={() => { update({ mathTopic }); setShowMathTopics(false); }}
              >{MATH_TOPIC_LABELS[mathTopic]}</Button>)}
            </View>
          </View>)}
        </View>}
      </>}
      <Text className={styles.hint}>
        {CATEGORY_LABELS[value.questionCategory]} · {questionsIn(value.questionCategory, value.mathTopic).length}题。
        {{ all: '混合抽取计算与常识。', math: '分数列式，练习增长、比重、分数与平均量。',
          culture: '科学、人文、地理与计算机知识，只做知识辨析，不计算。' }[value.questionCategory]}
        提交答案后可查看解析。
      </Text>
      </>}
      <Text className={styles.heading}>画面与声音</Text>
      {toggle('显示剩余张数', 'showRemaining')}
      {toggle('流畅动画', 'animationsEnabled')}
      {toggle('音效', 'soundEnabled')}
      {toggle('背景音乐', 'musicEnabled')}
    </View>
  );
};
export default SettingsPanel;
